import { format } from 'date-fns';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { TtfrAnalyticsDto } from '@/Domain/api/Analytics/dto/TtfrAnalytics.dto';
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
  CardContent,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationButton,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import { PaginationHelper } from '@/Domain/helper/Pagination.helper';

const AGENT_PAGE_SIZE = 5;

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.round(seconds / 60);
    return m >= 60 ? '1h' : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (m >= 60) return `${h + 1}h`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const computeNiceTicks = (
  data: { median_seconds: number }[],
): { ticks: number[]; domain: [number, number] } => {
  const maxVal = Math.max(...data.map((d) => d.median_seconds), 0);

  let interval: number;
  if (maxVal <= 120) interval = 30;
  else if (maxVal <= 600) interval = 120;
  else if (maxVal <= 3600) interval = 900;
  else if (maxVal <= 14400) interval = 3600;
  else if (maxVal <= 86400) interval = 21600;
  else interval = 86400;

  const ceiling = Math.ceil(maxVal / interval) * interval;
  const tickCount = ceiling / interval;
  const ticks: number[] = [];
  for (let i = 0; i <= tickCount; i++) {
    ticks.push(i * interval);
  }
  return { ticks, domain: [0, ceiling || interval] };
};

type Props = {
  data: TtfrAnalyticsDto | undefined;
  isLoading: boolean;
};

export const TtfrSection = ({ data, isLoading }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.ttfr' });

  const hasData = data && data.total_conversations > 0;

  const [agentPage, setAgentPage] = useState(0);
  const agents = data?.ttfr_by_agent ?? [];
  const totalAgentPages = Math.ceil(agents.length / AGENT_PAGE_SIZE);
  const currentAgentPage = agentPage + 1;
  const pagedAgents = agents.slice(agentPage * AGENT_PAGE_SIZE, (agentPage + 1) * AGENT_PAGE_SIZE);
  const agentPageRange = useMemo(
    () => PaginationHelper.getRange(currentAgentPage, totalAgentPages),
    [currentAgentPage, totalAgentPages],
  );

  const { ticks: yTicks, domain: yDomain } = hasData
    ? computeNiceTicks(data.ttfr_by_day)
    : { ticks: [0], domain: [0, 1] as [number, number] };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('description')}</p>
      </div>

      {!hasData ? (
        <ChartPlaceholder
          icon="mingcute:time-line"
          titleTranslationKey="no_data_in_range.title"
          descriptionTranslationKey="no_data_in_range.description"
          spacing="small"
        />
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardDescription>{t('median_ttfr')}</CardDescription>
                <CardTitle className="text-2xl">
                  {data.median_ttfr_seconds != null
                    ? formatDuration(data.median_ttfr_seconds)
                    : '—'}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>{t('total_handoffs')}</CardDescription>
                <CardTitle className="text-2xl">{data.total_conversations}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Daily chart */}
          <Card>
            <CardHeader>
              <CardTitle>{t('daily_median')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  median_seconds: { label: t('median_ttfr'), color: 'var(--chart-primary)' },
                }}
                className={`h-60 w-full ${isLoading ? 'animate-pulse' : ''}`}
              >
                <AreaChart
                  accessibilityLayer
                  data={data.ttfr_by_day}
                  margin={{ left: 0, right: 12 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => format(new Date(value), 'dd MMM')}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => formatDuration(value)}
                    width={56}
                    ticks={yTicks}
                    domain={yDomain}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
                        formatter={(value) => formatDuration(Number(value))}
                      />
                    }
                  />
                  <Area
                    dataKey="median_seconds"
                    type="monotone"
                    fill="var(--color-median_seconds)"
                    fillOpacity={0.2}
                    stroke="var(--color-median_seconds)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Per-agent table */}
          {agents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('by_agent')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium">{t('agent_email')}</th>
                        <th className="pb-2 font-medium text-right">{t('count')}</th>
                        <th className="pb-2 font-medium text-right">{t('median_ttfr')}</th>
                        <th className="pb-2 font-medium text-right">{t('avg_ttfr')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedAgents.map((agent) => (
                        <tr key={agent.email} className="border-b last:border-0">
                          <td className="py-2">{agent.email}</td>
                          <td className="py-2 text-right">{agent.count}</td>
                          <td className="py-2 text-right">
                            {formatDuration(agent.median_seconds)}
                          </td>
                          <td className="py-2 text-right">
                            {formatDuration(agent.average_seconds)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalAgentPages > 1 && (
                  <div className="flex items-center justify-center pt-4 gap-3">
                    <Pagination>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setAgentPage((p) => Math.max(0, p - 1))}
                          disabled={agentPage === 0}
                        />
                      </PaginationItem>

                      <PaginationContent>
                        {agentPageRange.map((page, idx) =>
                          page === '...' ? (
                            <PaginationEllipsis key={`ellipsis-${idx}`} />
                          ) : (
                            <PaginationItem key={page} onClick={() => setAgentPage(page - 1)}>
                              <PaginationButton size="icon" isActive={page === currentAgentPage}>
                                {page}
                              </PaginationButton>
                            </PaginationItem>
                          ),
                        )}
                      </PaginationContent>

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setAgentPage((p) => Math.min(totalAgentPages - 1, p + 1))}
                          disabled={agentPage >= totalAgentPages - 1}
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
